import React, { useState, useEffect } from 'react';
import { Layout } from '../components/Layout';
import api from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Shield, Eye, Pencil, Trash2, Download, UserX, LogIn,
  ChevronLeft, ChevronRight, Loader2, FileText,
} from 'lucide-react';
import { toast } from 'sonner';

const ACTION_ICONS = {
  view: Eye, create: FileText, edit: Pencil, delete: Trash2,
  export: Download, login: LogIn, logout: LogIn,
  deletion_request: UserX, anonymise: UserX,
};

const ACTION_COLORS = {
  view: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
  create: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400',
  edit: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
  delete: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
  export: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400',
  login: 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400',
  logout: 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400',
  deletion_request: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
  anonymise: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
};

export const AuditLog = () => {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [filterAction, setFilterAction] = useState('all');
  const [filterResource, setFilterResource] = useState('all');

  const fetchLog = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit: 50 });
      if (filterAction !== 'all') params.append('action', filterAction);
      if (filterResource !== 'all') params.append('resource_type', filterResource);
      const res = await api.get(`/gdpr/audit-log?${params}`);
      setEntries(res.data.entries);
      setTotalPages(res.data.pages);
      setTotal(res.data.total);
    } catch {
      toast.error('Failed to load audit log');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchLog(); }, [page, filterAction, filterResource]);

  const formatTime = (ts) => {
    if (!ts) return '--';
    const d = new Date(ts);
    return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) +
      ' ' + d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
  };

  return (
    <Layout>
      <div className="space-y-6" data-testid="audit-log-page">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 dark:text-white font-['Manrope']">Audit Log</h1>
            <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
              GDPR compliance — full record of data access and modifications ({total} entries)
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Select value={filterAction} onValueChange={(v) => { setFilterAction(v); setPage(1); }}>
              <SelectTrigger className="w-36 h-9" data-testid="filter-action">
                <SelectValue placeholder="All Actions" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Actions</SelectItem>
                {['view', 'create', 'edit', 'delete', 'export', 'login', 'deletion_request', 'anonymise'].map(a => (
                  <SelectItem key={a} value={a}>{a.replace('_', ' ')}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={filterResource} onValueChange={(v) => { setFilterResource(v); setPage(1); }}>
              <SelectTrigger className="w-36 h-9" data-testid="filter-resource">
                <SelectValue placeholder="All Resources" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Resources</SelectItem>
                {['customer', 'lead', 'engineer', 'portal_user', 'settings', 'document', 'message'].map(r => (
                  <SelectItem key={r} value={r}>{r.replace('_', ' ')}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <Card className="border-slate-200 dark:border-slate-700">
          {loading ? (
            <CardContent className="py-12 text-center">
              <Loader2 className="w-6 h-6 animate-spin text-blue-600 mx-auto" />
            </CardContent>
          ) : entries.length === 0 ? (
            <CardContent className="py-12 text-center">
              <Shield className="w-10 h-10 text-slate-300 mx-auto mb-3" />
              <p className="text-sm text-slate-500">No audit entries found</p>
            </CardContent>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-slate-200 dark:border-slate-700">
                    {['Timestamp', 'User', 'Action', 'Resource', 'Resource ID', 'Details', 'IP'].map(h => (
                      <th key={h} className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wide px-4 py-3">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {entries.map((e, i) => {
                    const Icon = ACTION_ICONS[e.action] || Eye;
                    const color = ACTION_COLORS[e.action] || ACTION_COLORS.view;
                    return (
                      <tr key={i} className="border-b border-slate-100 dark:border-slate-800" data-testid={`audit-row-${i}`}>
                        <td className="px-4 py-2.5 text-xs text-slate-500 whitespace-nowrap">{formatTime(e.timestamp)}</td>
                        <td className="px-4 py-2.5 text-sm text-slate-900 dark:text-white">{e.user_email}</td>
                        <td className="px-4 py-2.5">
                          <Badge className={`${color} gap-1 text-xs`}>
                            <Icon className="w-3 h-3" />
                            {e.action}
                          </Badge>
                        </td>
                        <td className="px-4 py-2.5 text-xs text-slate-600 dark:text-slate-400">{e.resource_type}</td>
                        <td className="px-4 py-2.5 text-xs text-slate-500 font-mono max-w-[120px] truncate">{e.resource_id || '--'}</td>
                        <td className="px-4 py-2.5 text-xs text-slate-500 max-w-[200px] truncate">{e.details || '--'}</td>
                        <td className="px-4 py-2.5 text-xs text-slate-400 font-mono">{e.ip_address || '--'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {totalPages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-slate-200 dark:border-slate-700">
              <p className="text-xs text-slate-500">Page {page} of {totalPages}</p>
              <div className="flex items-center gap-1">
                <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)} data-testid="prev-page-btn">
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)} data-testid="next-page-btn">
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}
        </Card>
      </div>
    </Layout>
  );
};

export default AuditLog;
